import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Tabs } from '../components/ui/Tabs';
import { Badge } from '../components/ui/Badge';
import { mockStores, mockOrders, mockOrganizations } from '../utils/mockData';
import { calculateStoreStatus, getStatusColor } from '../utils/storeStatus';
import { Search as SearchIcon } from 'lucide-react';
export function Search() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  const [activeTab, setActiveTab] = useState('stores');
  // Search stores
  const storeResults = mockStores.map(store => ({
    ...store,
    status: calculateStoreStatus(store)
  })).filter(store => store.name.toLowerCase().includes(query.toLowerCase()) || store.organizationName.toLowerCase().includes(query.toLowerCase()) || store.slug.toLowerCase().includes(query.toLowerCase()));
  // Search orders
  const orderResults = mockOrders.filter(order => order.id.toLowerCase().includes(query.toLowerCase()) || order.customerName.toLowerCase().includes(query.toLowerCase()) || order.customerEmail.toLowerCase().includes(query.toLowerCase()) || order.storeName.toLowerCase().includes(query.toLowerCase()));
  // Search organizations
  const orgResults = mockOrganizations.filter(org => org.name.toLowerCase().includes(query.toLowerCase()) || org.contactName.toLowerCase().includes(query.toLowerCase()) || org.contactEmail.toLowerCase().includes(query.toLowerCase()));
  const tabs = [{
    id: 'stores',
    label: `Stores (${storeResults.length})`
  }, {
    id: 'orders',
    label: `Orders (${orderResults.length})`
  }, {
    id: 'organizations',
    label: `Organizations (${orgResults.length})`
  }];
  const totalResults = storeResults.length + orderResults.length + orgResults.length;
  return <div className="space-y-6" data-id="element-1163">
      <div data-id="element-1164">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1165">Search Results</h1>
        <p className="text-gray-600 mt-1" data-id="element-1166">
          {totalResults} result{totalResults !== 1 ? 's' : ''} for "{query}"
        </p>
      </div>

      {totalResults === 0 ? <Card data-id="element-1167">
          <div className="text-center py-12" data-id="element-1168">
            <SearchIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-1169" />
            <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1170">
              No results found
            </h3>
            <p className="text-gray-600" data-id="element-1171">
              Try searching with different keywords or check your spelling.
            </p>
          </div>
        </Card> : <>
          <Tabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} data-id="element-1172" />

          {/* Stores Tab */}
          {activeTab === 'stores' && <Card data-id="element-1173">
              {storeResults.length === 0 ? <div className="text-center py-8 text-gray-500" data-id="element-1174">
                  No stores found
                </div> : <div className="space-y-3" data-id="element-1175">
                  {storeResults.map(store => <div key={store.id} onClick={() => navigate(`/pop-up-stores/${store.id}`)} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 cursor-pointer transition-colors" data-id="element-1176">
                      <div className="flex-1" data-id="element-1177">
                        <h3 className="font-medium text-gray-900" data-id="element-1178">
                          {store.name}
                        </h3>
                        <p className="text-sm text-gray-600" data-id="element-1179">
                          {store.organizationName}
                        </p>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-1180">
                          {new Date(store.settings.openDate).toLocaleDateString()}{' '}
                          -{' '}
                          {new Date(store.settings.closeDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center space-x-4" data-id="element-1181">
                        <div className="text-right" data-id="element-1182">
                          <p className="text-sm font-medium text-gray-900" data-id="element-1183">
                            {store.stats.ordersCount} orders
                          </p>
                          <p className="text-sm text-gray-600" data-id="element-1184">
                            ${store.stats.revenue.toFixed(2)}
                          </p>
                        </div>
                        <Badge variant={getStatusColor(store.status)} data-id="element-1185">
                          {store.status.toUpperCase()}
                        </Badge>
                      </div>
                    </div>)}
                </div>}
            </Card>}

          {/* Orders Tab */}
          {activeTab === 'orders' && <Card data-id="element-1186">
              {orderResults.length === 0 ? <div className="text-center py-8 text-gray-500" data-id="element-1187">
                  No orders found
                </div> : <div className="space-y-3" data-id="element-1188">
                  {orderResults.map(order => <div key={order.id} onClick={() => navigate(`/orders/${order.id}`)} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 cursor-pointer transition-colors" data-id="element-1189">
                      <div className="flex-1" data-id="element-1190">
                        <h3 className="font-medium text-gray-900" data-id="element-1191">
                          Order #{order.id.split('_')[1]}
                        </h3>
                        <p className="text-sm text-gray-600" data-id="element-1192">
                          {order.customerName} • {order.storeName}
                        </p>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-1193">
                          {new Date(order.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center space-x-4" data-id="element-1194">
                        <div className="text-right" data-id="element-1195">
                          <p className="text-lg font-bold text-gray-900" data-id="element-1196">
                            ${order.total.toFixed(2)}
                          </p>
                        </div>
                        <Badge variant={order.status === 'completed' ? 'green' : 'blue'} data-id="element-1197">
                          {order.status.replace('-', ' ')}
                        </Badge>
                      </div>
                    </div>)}
                </div>}
            </Card>}

          {/* Organizations Tab */}
          {activeTab === 'organizations' && <Card data-id="element-1198">
              {orgResults.length === 0 ? <div className="text-center py-8 text-gray-500" data-id="element-1199">
                  No organizations found
                </div> : <div className="space-y-3" data-id="element-1200">
                  {orgResults.map(org => <div key={org.id} onClick={() => navigate(`/organizations/${org.id}`)} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:border-gray-300 hover:bg-gray-50 cursor-pointer transition-colors" data-id="element-1201">
                      <div className="flex-1" data-id="element-1202">
                        <h3 className="font-medium text-gray-900" data-id="element-1203">
                          {org.name}
                        </h3>
                        <p className="text-sm text-gray-600" data-id="element-1204">
                          {org.contactName} • {org.contactEmail}
                        </p>
                        <p className="text-xs text-gray-500 mt-1" data-id="element-1205">
                          {org.defaultDeliveryAddress}
                        </p>
                      </div>
                      <div className="text-right" data-id="element-1206">
                        <p className="text-sm font-medium text-gray-900" data-id="element-1207">
                          {org.storeCount} stores
                        </p>
                        <p className="text-xs text-gray-500" data-id="element-1208">
                          {org.defaultTimezone}
                        </p>
                      </div>
                    </div>)}
                </div>}
            </Card>}
        </>}
    </div>;
}